/**
 * File: overviewFiguresUtils.ts
 * Path: apps/admin/src/shared/utils/overviewFiguresUtils.ts
 * Description: Everything the Overview shows for a chosen date range, worked
 * out from the journals: the bank balance at the end, profit in the range
 * against the same dates a year earlier, money in and out by month or week,
 * who owes you and for how long, the biggest costs, and what needs doing.
 * Pure functions, no screen code.
 */

import {
  agedDebtors,
  booksFigures,
  daysBetween,
  financialYearEnd,
  formatCents,
  financialYearStart,
  isIncomeStatement,
  normaliseName,
  obligationStatus,
  trialBalance,
  type Journal,
  type LedgerAccount,
  type InvoiceLedger,
  type Obligation,
} from "@idk/config";
import { entryKind } from "./journalGroupsUtils";

export interface DateRange {
  from: string;
  to: string;
}

/* ---------------------------------------------------------------------------
 * Dates
 * ------------------------------------------------------------------------ */

const utc = (day: string) => new Date(`${day}T00:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);
export const addDays = (day: string, n: number) => iso(new Date(utc(day).getTime() + n * 86_400_000));
const lastOfMonth = (year: number, month: number) => iso(new Date(Date.UTC(year, month, 0)));
const firstOfMonth = (day: string) => `${day.slice(0, 7)}-01`;

/** The same dates one year earlier (29 Feb becomes 28 Feb). */
export const yearBefore = (day: string): string => {
  const [y, m, d] = day.split("-").map(Number);
  const last = Number(lastOfMonth(y - 1, m).slice(8, 10));
  return `${y - 1}-${String(m).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
};

export type PresetId = "thisYear" | "lastYear" | "thisMonth" | "lastMonth" | "thisQuarter" | "last12" | "allTime";

export const PRESETS: { id: PresetId; label: string }[] = [
  { id: "thisYear", label: "This financial year" },
  { id: "lastYear", label: "Last financial year" },
  { id: "thisMonth", label: "This month" },
  { id: "lastMonth", label: "Last month" },
  { id: "thisQuarter", label: "This quarter" },
  { id: "last12", label: "Last 12 months" },
  { id: "allTime", label: "All time" },
];

/** A preset as dates. A range never runs past today, and never before the books start. */
export const presetRange = (id: PresetId, today: string, yearEndMonth: number, booksStart: string): DateRange => {
  const yearStart = financialYearStart(today, yearEndMonth);
  const [y, m] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
  let range: DateRange;
  switch (id) {
    case "thisYear":
      range = { from: yearStart, to: today };
      break;
    case "lastYear": {
      const to = addDays(yearStart, -1);
      range = { from: financialYearStart(to, yearEndMonth), to };
      break;
    }
    case "thisMonth":
      range = { from: firstOfMonth(today), to: today };
      break;
    case "lastMonth": {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      range = { from: `${py}-${String(pm).padStart(2, "0")}-01`, to: lastOfMonth(py, pm) };
      break;
    }
    case "thisQuarter": {
      const qm = Math.floor((m - 1) / 3) * 3 + 1;
      range = { from: `${y}-${String(qm).padStart(2, "0")}-01`, to: today };
      break;
    }
    case "last12":
      range = { from: addDays(yearBefore(today), 1), to: today };
      break;
    default:
      range = { from: booksStart, to: today };
  }
  return { from: range.from < booksStart ? booksStart : range.from, to: range.to > today ? today : range.to };
};

/** "1 Mar 2026". */
export const shortDate = (day: string): string => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(day.slice(8, 10))} ${months[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;
};

/* ---------------------------------------------------------------------------
 * Figures
 * ------------------------------------------------------------------------ */

const incomeStatementProfit = (journals: readonly Journal[], byId: Map<string, LedgerAccount>, range: DateRange): number => {
  let profit = 0;
  for (const j of journals) {
    if (j.date < range.from || j.date > range.to) continue;
    for (const l of j.lines) {
      const a = byId.get(l.accountId);
      if (a && isIncomeStatement(a.type)) profit += l.creditCents - l.debitCents;
    }
  }
  return profit;
};

export interface Headline {
  bankCents: number;
  /** Bank balance a month before the end of the range, for "since 1 Oct". */
  bankChangeCents: number;
  owedToYouCents: number;
  oweCents: number;
  directorLoanCents: number;
  profitCents: number;
  /** Profit over the same dates a year earlier. */
  profitBeforeCents: number;
  /** Null when the books didn't exist a year earlier, so no comparison is honest. */
  profitChangePercent: number | null;
}

export const headline = (
  journals: readonly Journal[],
  accounts: readonly LedgerAccount[],
  range: DateRange,
  yearEndMonth: number,
  booksStart: string,
): Headline => {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const at = (day: string) => booksFigures(trialBalance(journals, accounts, day, yearEndMonth));
  const now = at(range.to);
  const monthStart = firstOfMonth(range.to);
  const before = at(addDays(monthStart, -1));
  const profitCents = incomeStatementProfit(journals, byId, range);
  const previous: DateRange = { from: yearBefore(range.from), to: yearBefore(range.to) };
  const profitBeforeCents = incomeStatementProfit(journals, byId, previous);
  const comparable = previous.from >= booksStart && profitBeforeCents > 0;
  return {
    bankCents: now.bankCents,
    bankChangeCents: now.bankCents - before.bankCents,
    owedToYouCents: now.owedToYouCents,
    oweCents: now.oweCents,
    directorLoanCents: now.directorLoanCents,
    profitCents,
    profitBeforeCents,
    profitChangePercent: comparable ? Math.round(((profitCents - profitBeforeCents) / profitBeforeCents) * 100) : null,
  };
};

export type Grain = "month" | "week";

export interface FlowPoint {
  /** First day of the bucket. */
  start: string;
  label: string;
  inCents: number;
  outCents: number;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Monday of the week the day falls in. */
const weekStart = (day: string) => {
  const dow = (utc(day).getUTCDay() + 6) % 7;
  return addDays(day, -dow);
};

/**
 * Money in and out of the bank and cash accounts, bucketed by month or week.
 * A journal counts by its net effect on money accounts, so moving money
 * between your own accounts adds nothing either way.
 */
export const cashFlow = (
  journals: readonly Journal[],
  accounts: readonly LedgerAccount[],
  range: DateRange,
  grain: Grain,
): FlowPoint[] => {
  const money = new Set(accounts.filter((a) => a.money).map((a) => a.id));
  const keyOf = (day: string) => (grain === "month" ? firstOfMonth(day) : weekStart(day));
  const points = new Map<string, FlowPoint>();
  const make = (start: string): FlowPoint => ({
    start,
    label: grain === "month" ? MONTHS[Number(start.slice(5, 7)) - 1] : `${Number(start.slice(8, 10))} ${MONTHS[Number(start.slice(5, 7)) - 1]}`,
    inCents: 0,
    outCents: 0,
  });
  // Every bucket in the range exists, so a quiet month shows as nothing, not as a gap.
  for (let day = keyOf(range.from), guard = 0; day <= range.to && guard < 400; guard += 1) {
    points.set(day, make(day));
    day = grain === "month" ? lastOfMonthAfter(day) : addDays(day, 7);
  }
  for (const j of journals) {
    if (j.date < range.from || j.date > range.to) continue;
    const net = j.lines.filter((l) => money.has(l.accountId)).reduce((s, l) => s + l.debitCents - l.creditCents, 0);
    if (net === 0) continue;
    const point = points.get(keyOf(j.date));
    if (!point) continue;
    if (net > 0) point.inCents += net;
    else point.outCents += -net;
  }
  return [...points.values()].sort((a, b) => a.start.localeCompare(b.start));
};

const lastOfMonthAfter = (firstDay: string) => addDays(lastOfMonth(Number(firstDay.slice(0, 4)), Number(firstDay.slice(5, 7))), 1);

/** A chart axis that fits the data: round steps from nil, as few as it takes to pass the top value (at most five). */
export const niceAxis = (maxCents: number): { topCents: number; steps: number[] } => {
  if (maxCents <= 0) return { topCents: 100_00, steps: [0, 20_00, 40_00, 60_00, 80_00, 100_00] };
  const rand = maxCents / 100;
  const magnitude = 10 ** Math.floor(Math.log10(rand));
  const unit = [0.5, 1, 2, 2.5, 5, 10].map((f) => f * magnitude).find((u) => Math.ceil(rand / u) <= 5) ?? magnitude * 10;
  const count = Math.max(2, Math.ceil(rand / unit));
  const steps = Array.from({ length: count + 1 }, (_, i) => Math.round(unit * i * 100));
  return { topCents: steps[count], steps };
};

export interface Debtor {
  party: string;
  owedCents: number;
  /** Days since the oldest invoice still unpaid (oldest invoices are taken as paid first). */
  ageDays: number;
  oldestOn: string;
}

/** Who owes the business, per customer name, at the end of the range. */
export const debtors = (journals: readonly Journal[], accounts: readonly LedgerAccount[], asAt: string): Debtor[] => {
  const debtorIds = new Set(accounts.filter((a) => a.role === "debtors").map((a) => a.id));
  const byParty = new Map<string, { billed: { date: string; cents: number }[]; paid: number }>();
  const ordered = [...journals].filter((j) => j.date <= asAt).sort((a, b) => a.date.localeCompare(b.date) || a.number - b.number);
  for (const j of ordered) {
    for (const l of j.lines) {
      if (!debtorIds.has(l.accountId)) continue;
      const party = j.party ?? "No name given";
      const entry = byParty.get(party) ?? { billed: [], paid: 0 };
      if (l.debitCents > 0) entry.billed.push({ date: j.date, cents: l.debitCents });
      if (l.creditCents > 0) entry.paid += l.creditCents;
      byParty.set(party, entry);
    }
  }
  const out: Debtor[] = [];
  for (const [party, { billed, paid }] of byParty) {
    let remainingPaid = paid;
    let oldestOn: string | null = null;
    let owed = 0;
    for (const b of billed) {
      const used = Math.min(remainingPaid, b.cents);
      remainingPaid -= used;
      const left = b.cents - used;
      if (left > 0) {
        owed += left;
        oldestOn ??= b.date;
      }
    }
    if (owed > 0 && oldestOn) out.push({ party, owedCents: owed, ageDays: Math.max(0, daysBetween(oldestOn, asAt)), oldestOn });
  }
  return out.sort((a, b) => b.ageDays - a.ageDays || b.owedCents - a.owedCents);
};

/**
 * Who owes the business by the invoices in the books (invoiceLedger.ts), the
 * same figure Money and Aged debtors show: each client's unpaid balance, how
 * old their oldest unpaid invoice is, and whether any of their invoices is
 * past its due date.
 */
export const ledgerDebtors = (ledger: InvoiceLedger): { debtors: Debtor[]; lateness: DebtorLateness } => {
  const report = agedDebtors(ledger);
  return {
    debtors: report.rows
      .filter((r) => r.totalCents > 0)
      .map((r) => ({ party: r.party, owedCents: r.totalCents, ageDays: r.oldestDays, oldestOn: addDays(ledger.asAt, -r.oldestDays) })),
    lateness: new Map(report.rows.map((r) => [normaliseName(r.party), r.totalCents - r.bucketCents[0] > 0])),
  };
};

export interface Cost {
  name: string;
  cents: number;
}

/** The biggest expense accounts in the range, largest first. */
export const biggestCosts = (
  journals: readonly Journal[],
  accounts: readonly LedgerAccount[],
  range: DateRange,
  limit = 3,
): { costs: Cost[]; totalCents: number } => {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const totals = new Map<string, number>();
  for (const j of journals) {
    if (j.date < range.from || j.date > range.to) continue;
    for (const l of j.lines) {
      const a = byId.get(l.accountId);
      if (a?.type === "expense") totals.set(a.name, (totals.get(a.name) ?? 0) + l.debitCents - l.creditCents);
    }
  }
  const all = [...totals.entries()].map(([name, cents]) => ({ name, cents })).filter((c) => c.cents > 0);
  return { costs: all.sort((a, b) => b.cents - a.cents).slice(0, limit), totalCents: all.reduce((s, c) => s + c.cents, 0) };
};

/** Entries from your own typing that have no receipt kept: imported history is never counted. */
export const missingReceipts = (
  journals: readonly Journal[],
  accounts: readonly LedgerAccount[],
  range: DateRange,
): number => {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  return journals.filter(
    (j) =>
      j.date >= range.from &&
      j.date <= range.to &&
      j.source === "manual" &&
      !j.reversedBy &&
      j.documents.length === 0 &&
      entryKind(j, byId) === "expenses",
  ).length;
};

export type AttentionTone = "red" | "amber" | "blue";

export interface AttentionItem {
  id: string;
  title: string;
  detail: string;
  tag: string;
  tone: AttentionTone;
  /** Where "open" goes. */
  to: "reports" | "entries" | "tax";
}

/** With no invoice due date to go by (the personal books), money unpaid for longer than this many days is called overdue. */
export const OVERDUE_AFTER_DAYS = 30;

/**
 * Whether each client is late, by client name as the debtors list spells it:
 * late when one of their invoices is past its due date (ledgerDebtors).
 */
export type DebtorLateness = ReadonlyMap<string, boolean>;

/** Overdue by the invoices' due dates when known; otherwise by the 30-day rule. */
export const debtorIsLate = (debtor: Pick<Debtor, "party" | "ageDays">, lateness?: DebtorLateness | null): boolean =>
  lateness?.get(normaliseName(debtor.party)) ?? debtor.ageDays > OVERDUE_AFTER_DAYS;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The few things worth doing next, most urgent first. */
export const attentionItems = (input: {
  suspenseCents: number;
  debtors: readonly Debtor[];
  /** Which clients are late by their invoices' due dates; unknown clients fall back to the 30-day rule. */
  lateness?: DebtorLateness | null;
  missingReceipts: number;
  obligations: readonly Obligation[];
  today: string;
}): AttentionItem[] => {
  const items: AttentionItem[] = [];
  if (input.suspenseCents > 0) {
    items.push({
      id: "suspense",
      title: `${formatCents(input.suspenseCents)} needs a name`,
      detail: "Money sitting in Suspense until you say what it was.",
      tag: "Fix",
      tone: "amber",
      to: "reports",
    });
  }
  for (const d of input.debtors.filter((x) => debtorIsLate(x, input.lateness)).slice(0, 2)) {
    items.push({
      id: `debtor-${d.party}`,
      title: `${d.party} is overdue`,
      detail: `${formatCents(d.owedCents)} unpaid for ${d.ageDays} days.`,
      tag: "Overdue",
      tone: "red",
      to: "entries",
    });
  }
  if (input.missingReceipts > 0) {
    items.push({
      id: "receipts",
      title: `${plural(input.missingReceipts, "expense has", "expenses have")} no receipt`,
      detail: "Attach a photo so the books can be proved.",
      tag: "Receipts",
      tone: "amber",
      to: "entries",
    });
  }
  const due = input.obligations
    .filter((o) => o.applies && !o.filedOn && !o.optional && daysBetween(input.today, o.dueOn) <= 30)
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn))
    .slice(0, 3);
  for (const o of due) {
    const overdue = obligationStatus(o, input.today) === "overdue";
    const days = daysBetween(input.today, o.dueOn);
    items.push({
      id: `filing-${o.id}`,
      title: o.title,
      detail: overdue ? `Was due ${shortDate(o.dueOn)}.` : `Due ${shortDate(o.dueOn)}, in ${plural(days, "day", "days")}.`,
      tag: overdue ? "Overdue" : "Due soon",
      tone: overdue ? "red" : "blue",
      to: "tax",
    });
  }
  return items;
};

export { financialYearEnd };
